/**
 * Invoicing Incidents Page (list)
 *
 * Controlled process to review documents with electronic-invoicing problems
 * (submission errors, Hacienda rejections, connection errors). Each row opens
 * a dedicated detail page (/settings/invoicing-incidents/:id) where the
 * incident is worked. Data is fetched in real time (no cache).
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  Loader2,
  RefreshCw,
  ScanLine,
} from 'lucide-react'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/toaster'
import { useAuth } from '@/hooks/useAuth'
import {
  IncidentSummary,
  IncidentWorkflowStatus,
  useIncidents,
  useIncidentStats,
  useScanIncidents,
} from '@/hooks/useInvoicing'
import { cn } from '@/lib/utils'
import { t } from '@/i18n'
import {
  CATEGORY_ORDER,
  CATEGORY_PRESENTATION,
  FriendlyErrors,
  KIND_LABELS,
  TYPE_LABELS,
  TypeIcon,
  WORKFLOW_CONFIG,
  HaciendaStatusBadge,
  formatCurrency,
  formatDate,
} from './incidentShared'

export function InvoicingIncidentsPage() {
  const navigate = useNavigate()
  const { isAdmin } = useAuth()
  const { toast } = useToast()

  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('active')
  const [typeFilter, setTypeFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [sort, setSort] = useState('date_desc')
  const [search, setSearch] = useState('')
  const pageSize = 20

  const { data, isLoading, refetch, isFetching } = useIncidents({
    page,
    size: pageSize,
    status: statusFilter,
    incident_type: typeFilter,
    error_category: categoryFilter,
    sort,
    search,
  })
  const { data: stats } = useIncidentStats()
  const scan = useScanIncidents()

  if (!isAdmin) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">{t('incidents.noPermission')}</p>
      </div>
    )
  }

  const handleScan = () => {
    scan.mutate(undefined, {
      onSuccess: (res) => {
        toast({ title: t('incidents.scanDone'), description: res.message })
        refetch()
      },
      onError: () => {
        toast({ title: t('incidents.scanError'), variant: 'destructive' })
      },
    })
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} aria-label={t('common.back')}>
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </Button>
        <div className="flex-1 min-w-[12rem]">
          <h1 className="text-xl font-bold tracking-tight">{t('incidents.title')}</h1>
          <p className="text-sm text-muted-foreground truncate">
            {stats
              ? t('incidents.activeInView', { active: stats.active, total: data?.total ?? 0 })
              : t('incidents.inView', { total: data?.total ?? 0 })}
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching} aria-label={t('common.refresh')}>
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} aria-hidden="true" />
        </Button>
        <Button onClick={handleScan} disabled={scan.isPending}>
          {scan.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
          ) : (
            <ScanLine className="h-4 w-4 mr-2" aria-hidden="true" />
          )}
          {t('incidents.scan')}
        </Button>
      </div>

      {/* Stats chips */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {(['open', 'in_progress', 'resolved', 'dismissed'] as IncidentWorkflowStatus[]).map(
            (s) => (
              <Card key={s}>
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold tabular-nums">{stats[s]}</p>
                  <p className="text-xs text-muted-foreground">{WORKFLOW_CONFIG[s].label}</p>
                </CardContent>
              </Card>
            )
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <Input
          placeholder={t('incidents.searchPlaceholder')}
          aria-label={t('incidents.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
        />
        <div className="grid grid-cols-2 gap-2">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v)
              setPage(1)
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('common.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{t('incidents.filterActive')}</SelectItem>
              <SelectItem value="all">{t('incidents.filterAll')}</SelectItem>
              <SelectItem value="open">{t('incidents.filterOpen')}</SelectItem>
              <SelectItem value="in_progress">{t('incidents.filterInProgress')}</SelectItem>
              <SelectItem value="resolved">{t('incidents.filterResolved')}</SelectItem>
              <SelectItem value="dismissed">{t('incidents.filterDismissed')}</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={typeFilter}
            onValueChange={(v) => {
              setTypeFilter(v)
              setPage(1)
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('incidents.type')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('incidents.allTypes')}</SelectItem>
              <SelectItem value="submission_error">{t('incidents.typeSubmissionError')}</SelectItem>
              <SelectItem value="rejected">{t('incidents.typeRejected')}</SelectItem>
              <SelectItem value="connection_error">{t('incidents.typeConnectionError')}</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={categoryFilter}
            onValueChange={(v) => {
              setCategoryFilter(v)
              setPage(1)
            }}
          >
            <SelectTrigger className="col-span-2">
              <SelectValue placeholder={t('incidents.errorCategory')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('incidents.allErrorCategories')}</SelectItem>
              {CATEGORY_ORDER.map((slug) => {
                const p = CATEGORY_PRESENTATION[slug]
                const count = stats?.by_category?.[slug] ?? 0
                // Skip categories without active errors, unless the user is
                // already filtering by that one (so the trigger label stays
                // consistent even after the count drops to 0).
                if (count === 0 && categoryFilter !== slug) return null
                return (
                  <SelectItem key={slug} value={slug}>
                    {p.label}
                    {count > 0 ? ` (${count})` : ''}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
          <Select
            value={sort}
            onValueChange={(v) => {
              setSort(v)
              setPage(1)
            }}
          >
            <SelectTrigger className="col-span-2">
              <SelectValue placeholder={t('incidents.order')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date_desc">{t('incidents.newestFirst')}</SelectItem>
              <SelectItem value="date_asc">{t('incidents.oldestFirst')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : !data?.items.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" aria-hidden="true" />
            <p className="text-muted-foreground">{t('incidents.empty')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('incidents.emptyHint')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {data.items.map((inc) => (
              <IncidentCard key={inc.id} incident={inc} />
            ))}
          </div>

          {data.total > pageSize && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-sm text-muted-foreground">
                {t('incidents.range', { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, data.total), total: data.total })}
              </p>
              <Pagination
                currentPage={page}
                totalPages={Math.ceil(data.total / pageSize)}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

function IncidentCard({ incident }: { incident: IncidentSummary }) {
  const wf = WORKFLOW_CONFIG[incident.status]
  return (
    <Card className="relative overflow-hidden hover:bg-accent/40 transition-colors focus-within:ring-2 focus-within:ring-ring">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2 gap-2">
          <Badge variant="outline" className="gap-1 text-xs">
            <TypeIcon type={incident.incident_type} />
            {TYPE_LABELS[incident.incident_type] || incident.incident_type}
          </Badge>
          <Badge variant="secondary" className={cn('text-xs', wf.color)}>
            {wf.label}
          </Badge>
        </div>

        <p className="font-medium truncate">{incident.client_name || t('incidents.generalClient')}</p>
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2 gap-2">
          <span className="flex items-center gap-2 min-w-0">
            <span className="truncate">{KIND_LABELS[incident.document_kind] || incident.document_kind}</span>
            <HaciendaStatusBadge status={incident.current_hacienda_status} />
          </span>
          <span className="tabular-nums font-medium text-foreground shrink-0">
            {formatCurrency(incident.total)}
          </span>
        </div>

        {incident.consecutive && (
          <p className="text-xs font-mono text-muted-foreground truncate">{incident.consecutive}</p>
        )}
        {incident.errors.length > 0 && (
          <div className="mt-2">
            <FriendlyErrors errors={incident.errors} variant="compact" />
          </div>
        )}

        <div className="flex items-center justify-between mt-3">
          {/* La fecha del documento, no la del scan: last_seen_at se reescribe
              en cada pasada y hacia que todo apareciera con la fecha de hoy. */}
          <span className="text-xs text-muted-foreground">
            {formatDate(incident.document_date ?? incident.first_detected_at)}
          </span>
          {/* El enlace cubre toda la tarjeta (after:inset-0): se abre con teclado y con Ctrl+clic */}
          <Button variant="outline" size="sm" asChild>
            <Link
              to={`/settings/invoicing-incidents/${incident.id}`}
              className="after:absolute after:inset-0 focus-visible:outline-none"
            >
              <Eye className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('incidents.work')}
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
